import Tooltip from '@material-ui/core/Tooltip';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import './BrProductCardV1.scss';
import BrTooltip from '@components/BrTooltip';
import BrText from '@components/BrText';
import BrIcons from '@components/BrIcons';

const getTooltip = (element, tooltipText, useBrTooltip, location, isSku) => {
  if (useBrTooltip) {
    const displayElement = isSku ? (
      <span className={classnames(isSku && 'br-pc-v1-info-details-inline')}>
        <div className={classnames(isSku && 'br-pc-v1-info-details-inline-padding')}>{element}</div>
        {isSku && <BrIcons.GreaterThanLight />}
      </span>
    ) : (
      element
    );
    return (
      <BrTooltip
        isDark
        className={classnames('br-pc-v1-custom-tooltip', isSku && 'custom-right')}
        location={location || BrTooltip.Locations.top}
      >
        {displayElement}
        <BrTooltip.Tooltip>
          <span>{tooltipText}</span>
        </BrTooltip.Tooltip>
      </BrTooltip>
    );
  }
  return (
    <div>
      {tooltipText && (
        <Tooltip arrow title={<span style={{ fontSize: '12px' }}>{tooltipText}</span>}>
          {element}
        </Tooltip>
      )}
    </div>
  );
};

const getPriceLabel = (price, currency, crossed) => {
  return (
    <div className={classnames('br-pc-v1-price-label', crossed && 'br-pc-v1-price-label-strike')}>
      {currency}
      {price}
    </div>
  );
};

const getLeftBottomLabels = (price, salePrice, currency) => {
  const displayElement = [];
  if (price || salePrice) {
    if (salePrice !== price) {
      displayElement.push(getPriceLabel(salePrice, currency, false));
    }
    displayElement.push(getPriceLabel(price, currency, salePrice !== price));
  }
  return displayElement;
};

export default function BrProductCardV1(props) {
  const {
    imgConfig: { imgSrc },
    title,
    details,
    footer,
    onClick,
    price,
    salePrice,
    currency,
    sequence,
    useBrTooltip,
    viewMore,
  } = props;
  const getClassName = (cn) => classnames(cn, onClick && 'clickable');
  return (
    <div className="br-product-card-v1">
      <div className={classnames('br-pc-v1-card-body', !footer && 'br-pc-v1-no-footer')}>
        <div className={getClassName('br-pc-v1-image-container')} onClick={onClick}>
          <div className="br-pc-v1-img-src-container">
            <img src={imgSrc} />
          </div>
          <div className="br-pc-v1-right-top-label">
            <div className="br-pc-v1-spaced-label">{sequence}</div>
          </div>
          <div className="br-pc-v1-left-bottom-label">{getLeftBottomLabels(price, salePrice, currency)}</div>
        </div>
        {getTooltip(
          <span className={getClassName('br-pc-v1-card-title')} onClick={onClick}>
            {title}
          </span>,
          title,
          useBrTooltip,
        )}
        <div className="br-pc-v1-card-info">
          {details
          && details.map((item, index) => {
            const titleElement = <BrText className="br-pc-v1-info-title">{item.title}</BrText>;
            const detailsElement = <div className="br-pc-v1-info-content">{item.details}</div>;
            return (
              <React.Fragment key={JSON.stringify(index)}>
                {item.details && (
                  <>
                    {item.titleTooltip ? getTooltip(titleElement, item.titleTooltip, useBrTooltip) : titleElement}
                    {item.detailsTooltip
                      ? getTooltip(
                        detailsElement,
                        item.detailsTooltip,
                        useBrTooltip,
                        item.detailsTooltipLocation,
                        item.isSku,
                      )
                      : detailsElement}
                  </>
                )}
              </React.Fragment>
            );
          })}
        </div>
        {viewMore && (
          <div className="br-pc-v1-view-more" onClick={viewMore}>
            View More
          </div>
        )}
      </div>
      {footer && <div className="br-pc-v1-card-footer">{footer}</div>}
    </div>
  );
}

/**
 * @param {{title:String, details:any, tooltip:any}} details List of items to be shown in the body of product card
 * @param {ReactNode} footer Footer to be displayed
 * @param {{imgSrc:String, leftBottomLabels:ReactNode, rightTopLabels: ReactNode}} imgConfig config details of image
 * @callback @param onClick callback for what happens when clicked on product card
 * @param {String} title title to be displayed for card
 */
BrProductCardV1.propTypes = {
  details: PropTypes.arrayOf(
    /**
     * @param {String} title title to be displayed against details inside the body of product card
     * @param {any} details content to be displayed in a row inside body of product card
     * @param {String} tooltip tooltip text to be displayed against title
     */
    PropTypes.shape({
      title: PropTypes.string,
      details: PropTypes.any,
      tooltip: PropTypes.string,
    }),
  ),
  footer: PropTypes.node,
  /**
   * @param {String} imgSrc img url
   * @param {ReactNode} leftBottomLabels array of Labels to be displayed on left bottom of image inside product card
   * @param {ReactNode} rightTopLabels array of Labels to be displayed on top right of image inside product card
   */
  imgConfig: PropTypes.shape({
    imgSrc: PropTypes.string,
  }),
  onClick: PropTypes.func,
  title: PropTypes.string,
};
